import React, { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Download, Trash2, ArrowLeft, LayoutTemplate } from "lucide-react";
import { AffidavitLayoutDialog } from "@/components/AffidavitLayoutDialog";
import { toast } from "sonner";
import { useSearchParams, Link } from "react-router-dom";

export default function Affidavits() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [rev, setRev] = useState(0);
  const [sp, setSp] = useSearchParams();
  const load = () => api.get("/affidavits").then((r) => {
    setList(r.data);
    const openId = sp.get("open");
    if (openId) setSelected(r.data.find((a) => a.id === openId) || null);
  });
  useEffect(() => { load(); }, []);

  const pdfUrl = (a) => `${api.defaults.baseURL}/affidavits/${a.id}/pdf`;
  const goBack = () => { setSelected(null); if (sp.has("open")) setSp({}, { replace: true }); };
  const allChecked = list.length > 0 && checked.size === list.length;
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(list.map((a) => a.id)));
  const toggleOne = (id) => setChecked((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const deleteChecked = async () => {
    const ids = [...checked];
    const { data } = await api.post("/affidavits/bulk-delete", { ids });
    toast.success(`Deleted ${data.deleted} affidavit${data.deleted === 1 ? "" : "s"}`);
    setChecked(new Set());
    setConfirmOpen(false);
    if (selected && ids.includes(selected.id)) goBack();
    load();
  };

  return (
    <>
      <PageHeader title="Affidavits" description="Court-ready proof of service generated from your attempt log. Download as PDF." />
      <div className="p-4 sm:p-7 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className={`lg:col-span-1 space-y-2 ${selected ? "hidden lg:block" : ""}`}>
          {list.length > 0 && (
            <div className="flex items-center gap-3 px-1 pb-1" data-testid="affidavit-select-bar">
              <Checkbox id="aff-select-all" data-testid="affidavit-select-all" checked={allChecked} onCheckedChange={toggleAll} />
              <label htmlFor="aff-select-all" className="text-xs text-slate-600 cursor-pointer select-none">
                {checked.size > 0 ? `${checked.size} selected` : "Select all"}
              </label>
              {checked.size > 0 && (
                <Button size="sm" variant="destructive" className="ml-auto h-7" data-testid="affidavit-delete-selected" onClick={() => setConfirmOpen(true)}>
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete ({checked.size})
                </Button>
              )}
            </div>
          )}
          {list.length === 0 ? (
            <div className="text-sm text-slate-400 py-8 text-center">No affidavits yet. Generate one from a job.</div>
          ) : list.map((a) => (
            <Card key={a.id} data-testid={`affidavit-card-${a.id}`}
              className={`border-slate-200 p-4 cursor-pointer ${selected?.id === a.id ? "border-[#2d7bd0] ring-1 ring-[#2d7bd0]/30" : ""}`}
              onClick={() => setSelected(a)}>
              <div className="flex items-center gap-2">
                <span onClick={(e) => e.stopPropagation()} className="flex items-center">
                  <Checkbox data-testid={`affidavit-check-${a.id}`} checked={checked.has(a.id)} onCheckedChange={() => toggleOne(a.id)} />
                </span>
                <FileText className="h-4 w-4 text-slate-500" />
                <span className="text-xs text-slate-500">{a.job_number}</span>
                <span className={`ml-auto text-[10px] uppercase font-semibold ${a.signed ? "text-emerald-700" : "text-amber-700"}`}>{a.signed ? "Signed" : "Unsigned"}</span>
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900 truncate">{a.recipient_name}</div>
              <div className="mt-1 text-xs text-slate-400">{new Date(a.created_at).toLocaleString()}</div>
            </Card>
          ))}
        </div>
        <div className="lg:col-span-2">
          {selected ? (
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Button size="sm" variant="ghost" className="-ml-2" data-testid="affidavit-back" onClick={goBack}>
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Affidavits
                </Button>
                <div className="text-sm text-slate-600">{({ posting: "Affidavit of Posting", non_service: "Affidavit of Non-Service" })[selected.kind] || "Affidavit of Service"} · <Link to={`/jobs/${selected.job_id}`} className="sm-link">{selected.job_number}</Link></div>
                <div className="ml-auto flex items-center gap-2">
                  {!selected.signed && (
                    <Button size="sm" variant="outline" data-testid="sign-affidavit" onClick={async () => {
                      const { data } = await api.post(`/affidavits/${selected.id}/sign`);
                      toast.success("Signed"); setSelected(data); load();
                    }}>Sign</Button>
                  )}
                  <Button size="sm" variant="outline" data-testid="layout-affidavit" onClick={() => setLayoutOpen(true)}><LayoutTemplate className="h-3.5 w-3.5 mr-1.5" /> Layout</Button>
                  <a href={pdfUrl(selected)} target="_blank" rel="noreferrer" data-testid="download-affidavit">
                    <Button size="sm" className="sm-green"><Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF</Button>
                  </a>
                </div>
              </div>
              <iframe key={`${selected.id}-${selected.signed}-${rev}`} title="Affidavit PDF" src={pdfUrl(selected)} className="w-full h-[78vh] border border-slate-200 rounded bg-white" data-testid="affidavit-pdf-preview" />
              {layoutOpen && <AffidavitLayoutDialog affidavitId={selected.id} onClose={() => setLayoutOpen(false)} onSaved={() => { setLayoutOpen(false); setRev((r) => r + 1); }} />}
            </div>
          ) : (
            <div className="hidden lg:block rounded-lg border border-dashed border-slate-300 py-24 text-center text-sm text-slate-400">Select an affidavit to preview.</div>
          )}
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="affidavit-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {checked.size} affidavit{checked.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the selected affidavit{checked.size === 1 ? "" : "s"}. You can regenerate them from the job at any time.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="affidavit-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="affidavit-delete-confirm-btn" className="bg-red-600 hover:bg-red-700" onClick={deleteChecked}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
